'use client';
import { useEffect, useMemo } from 'react';
import ActiveFilters from '@/components/products/active-filters/active-filters';
import MobileFilters from '@/components/products/mobile-filters/mobile-filters';
import ProductCardListing from '@/components/products/product-card/product-card-listing';
import ProductFilterDeferred from '@/components/products/product-filter/product-filter-deferred';
import { ProductFilterLazyProvider } from '@/components/products/product-filter/product-filter-lazy-provider';
import ProductPagination from '@/components/products/product-pagination.tsx/product-pagination';
import ProductsSortBy from '@/components/products/products-sort-by/products-sort-by';
import { BreadcrumbItem, Breadcrumbs, Link } from '@/components/ui';
import { useCookiebotConsent, useCookiebotReady } from '@/hooks/use-cookiebot-consent';
import useCoreConfig from '@/hooks/use-core-config';
import { usePriceFilterContext } from '@/hooks/use-price-filter-context';
import useProductListingSearchParams from '@/hooks/use-product-listing-search-params';
import useProductsPageContext from '@/hooks/use-products-page-context';
import useResetProductListingFiltersOnNavigate from '@/hooks/use-reset-product-listing-filters-on-navigate';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { SortByItemKeys } from '@/types/common/sort-by-item-keys';
import { createTrackingEventId } from '@/utils/capi-event';
import { postCapiCategoryPageView } from '@/utils/capi-tracking';
import { createFilters } from '@/utils/create-filters';
import { applyFilters, paginateProducts, sortProducts } from '@/utils/filtered-products';
import { getCategoryBreadcrumbs } from '@/utils/get-category-breadcrumbs';
import { getPriceFilter } from '@/utils/get-price-filter';
import { mapProductToSimpleProductInfo } from '@/utils/map-product-to-simple-product-info';
import { getProductListingAppliedFilters } from '@/utils/product-listing-search-params';
import {
  buildStudioProductsCategoryPath,
  buildStudioProductsListingPath,
  toStudioCategoryHref,
} from '@/utils/studio-preview-locale-url';
import FeatureFlag from '../common/feature-flag/feature-flag';
import NoNata from '../common/no-data/no-data';

const CATEGORY_VIEW_TRACK_DEDUPE_MS = 3_000;
const recentCategoryViewTracks = new Map<string, number>();

const shouldTrackCategoryView = (trackingKey: string): boolean => {
  const now = Date.now();
  const lastTrackedAt = recentCategoryViewTracks.get(trackingKey);

  if (lastTrackedAt !== undefined && now - lastTrackedAt < CATEGORY_VIEW_TRACK_DEDUPE_MS) {
    return false;
  }

  recentCategoryViewTracks.set(trackingKey, now);
  return true;
};

const ProductsPageContent = () => {
  const { t } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>() ?? {};
  const {
    category,
    products,
    subCategories,
    categoryFilters: serverCategoryFilters,
  } = useProductsPageContext();
  const hasMarketingConsent = useCookiebotConsent('marketing');
  const isCookiebotReady = useCookiebotReady();
  const priceFilterCtx = usePriceFilterContext();

  const { isAuthenticated } = useKeycloak();

  const searchParams = useProductListingSearchParams();
  useResetProductListingFiltersOnNavigate();
  const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
  const sortBy = (searchParams.get('sort-by') as SortByItemKeys) ?? SortByItemKeys.NONE;

  const sortedProducts = sortProducts(products.items, sortBy, priceFilterCtx);

  const appliedFilters = getProductListingAppliedFilters(searchParams, { isStudioPreview });
  const { currency, features } = useCoreConfig();

  const finalFilteredProducts = applyFilters(sortedProducts, appliedFilters, priceFilterCtx);

  const { paginatedProducts: currentProducts, totalPages } = paginateProducts(
    finalFilteredProducts,
    currentPage,
    25,
  );

  const dynamicFilters = useMemo(
    () => serverCategoryFilters ?? createFilters(category),
    [serverCategoryFilters, category],
  );
  const breadcrumbs = getCategoryBreadcrumbs(category);
  const productsListingHref = isStudioPreview ? buildStudioProductsListingPath() : '/products';
  const resolveCategoryHref = (storefrontPath: string) =>
    isStudioPreview ? (toStudioCategoryHref(storefrontPath) ?? storefrontPath) : storefrontPath;
  const priceFilter = useMemo(
    () => getPriceFilter(products.items, currency, t, priceFilterCtx),
    [products.items, currency, t, priceFilterCtx],
  );

  const productsPageLabel = t?.products.productsPage ?? 'Products';

  const categoryViewTrackingKey = useMemo(
    () => `${category.id}:${currentPage}:${searchParams.toString()}`,
    [category.id, currentPage, searchParams],
  );

  useEffect(() => {
    if (isStudioPreview || !isCookiebotReady || !category.id || currentProducts.length === 0) {
      return;
    }

    if (!shouldTrackCategoryView(categoryViewTrackingKey)) {
      return;
    }

    const eventId = createTrackingEventId();
    const sourceUrl = window.location.href;

    void import('@next/third-parties/google').then(({ sendGTMEvent }) => {
      sendGTMEvent({ ecommerce: null });
      sendGTMEvent({
        event: 'view_item_list',
        event_id: eventId,
        ecommerce: {
          currency,
          item_list_id: category.id,
          item_list_name: category.name,
          items: currentProducts.map((product, index) => ({
            item_id: product.id,
            item_name: product.name,
            item_category: category.name,
            price: product.discountedPrice,
            index,
          })),
        },
      });
    });

    if (hasMarketingConsent) {
      void postCapiCategoryPageView({
        id: eventId,
        sourceUrl,
        categoryId: category.id,
      });
    }
  }, [
    category.id,
    category.name,
    categoryViewTrackingKey,
    currency,
    currentProducts,
    hasMarketingConsent,
    isCookiebotReady,
    isStudioPreview,
  ]);

  return (
    <div>
      <div className={`mb-6 w-full`} data-testid="products-page__breadcrumbs">
        <Breadcrumbs>
          <BreadcrumbItem>
            <Link noStyle href={productsListingHref}>
              {productsPageLabel}
            </Link>
          </BreadcrumbItem>
          {breadcrumbs.map(item => (
            <BreadcrumbItem key={item.url}>
              <Link href={resolveCategoryHref(item.url)} noStyle>
                {item.label}
              </Link>
            </BreadcrumbItem>
          ))}
        </Breadcrumbs>
      </div>

      <h1 className="mb-6 text-4xl font-semibold" data-testid="all-products-page__heading">
        {breadcrumbs[breadcrumbs.length - 1].label}
      </h1>

      <div className={`flex w-full gap-4`} data-testid="products-page__content">
        <div className="hidden w-64 shrink-0 @lg:block" data-testid="products-page__filters">
          <ProductFilterLazyProvider>
            {subCategories && subCategories.length > 0 && (
              <div className="hidden w-64 @lg:block">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold">{category.name}</h2>
                  <ul>
                    {subCategories.map(subcategory => (
                      <li key={subcategory.id}>
                        <Link
                          href={
                            isStudioPreview
                              ? buildStudioProductsCategoryPath(subcategory.metaUrl)
                              : `/products/${subcategory.metaUrl}`
                          }
                          className="text-foreground mb-1 hover:underline"
                        >
                          {subcategory.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {((!isAuthenticated && features.priceForGuests) || isAuthenticated) &&
              features.priceFilter && (
                <FeatureFlag feature="priceFilter">
                  <ProductFilterDeferred filter={priceFilter} />
                </FeatureFlag>
              )}
            {dynamicFilters.map(filter => (
              <ProductFilterDeferred key={filter.id} filter={filter} />
            ))}
          </ProductFilterLazyProvider>
        </div>

        <div className="w-full @lg:overflow-hidden">
          <div
            className="mb-4 flex items-center gap-4 @lg:pt-2"
            data-testid="products-page__sort-filters"
          >
            {((!isAuthenticated && features.priceForGuests) || isAuthenticated) && (
              <ProductsSortBy />
            )}
            <div className="block @lg:hidden">
              <MobileFilters />
            </div>
            <div className="hidden @lg:block">
              <ActiveFilters />
            </div>

            <span className="ml-auto text-sm text-gray-700">
              {finalFilteredProducts.length === 1
                ? t?.products.itemsFound.one
                : t?.products.itemsFound.other.replace(
                    '{{count}}',
                    finalFilteredProducts.length.toString(),
                  )}
            </span>
          </div>

          {currentProducts.length !== 0 ? (
            <>
              <div
                className="grid grid-cols-2 gap-4 @md:grid-cols-3 @lg:grid-cols-3 @xl:grid-cols-4"
                data-testid="products-page__product-grid"
              >
                {currentProducts.map((product, index) => (
                  <ProductCardListing
                    key={product.id || product.sku || index}
                    product={mapProductToSimpleProductInfo(product)}
                    priority={index === 0}
                  />
                ))}
              </div>
              <div className="mt-8 flex justify-center" data-testid="products-page__pagination">
                <ProductPagination totalPages={totalPages} />
              </div>
            </>
          ) : (
            <NoNata
              label={t?.products.noProductFound ?? ''}
              className="my-52 text-black"
              data-testid="products-page__no-products"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsPageContent;
